import { AvatarWithStatusProps } from '@modules/talents/components/AvatarWithStatus';
import { RatingSelector } from '@modules/talents/components';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import { Box, Button, Checkbox, CircularProgress, IconButton, Typography, styled } from '@mui/material';
import React from 'react';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';

interface TalentFloatingActionBarProps {
    selectedTalentIds: string[];
    onSendMessage?: () => void;
    onUpdateRating?: (rating: number) => void;
    onStatusChange?: (status: string) => void;
    status: AvatarWithStatusProps['status'];
    isUpdatingRating?: boolean;
    isUpdatingStatus?: boolean;
    /** Map of talent ID to their upercutFavorites for multi-talent favorite handling */
    talentFavoritesMap?: Record<string, Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }>>;
}

// Styled Components
const FloatingActionBarContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: theme.palette.background.default,
    boxShadow: '0 4px 20px 0 rgba(255, 255, 255, 0.16)',
    borderRadius: '16px',
    padding: '8px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '48px',
    zIndex: 1000,
}));

const SelectedCountContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(60, 45, 225, 0.3)',
    borderRadius: '8px',
    padding: '4px 8px',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    '&:hover': {
        background: 'rgba(60, 45, 225, 0.5)',
    },
});

const SelectedCountText = styled(Typography)({
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '32px',
    whiteSpace: 'nowrap',
});

const ActionsContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
});

const StatusContainer = styled(Box)<{ isUpdating?: boolean }>(({ isUpdating }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    opacity: isUpdating ? 0.5 : 1,
}));

const RatingContainer = styled(Box)({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
});

const LoadingIndicator = styled(CircularProgress)({
    position: 'absolute',
    left: '50%',
    top: '50%',
    marginLeft: '-10px',
    marginTop: '-10px',
    color: '#5447DE',
});

const Divider = styled(Box)({
    width: '1px',
    height: '37px',
    background: 'rgba(255, 255, 255, 0.2)',
});

const JobOfferButton = styled(IconButton)({
    width: 48,
    height: 48,
    borderRadius: '48px',
    background: '#5447DE',
    '&:hover': {
        background: '#4239B0',
    },
});

const SendMessageButton = styled(Button)({
    height: 48,
    borderRadius: '48px',
    background: '#5447DE',
    padding: '8px 16px',
    textTransform: 'none',
    '&:hover': {
        background: '#4239B0',
    },
});

const SendMessageText = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    whiteSpace: 'nowrap',
}));

const StyledRatingSelector = styled(RatingSelector)<{ isUpdating?: boolean }>(({ theme, isUpdating }) => ({
    alignSelf: 'center',
    opacity: isUpdating ? 0.5 : 1,
}));

const BriefcaseIcon = styled('i')(({ theme }) => ({
    color: theme.palette.common.white,
    fontSize: '20px',
}));

const TalentFloatingActionBar: React.FC<TalentFloatingActionBarProps> = ({
    status,
    onSendMessage,
    onUpdateRating,
    onStatusChange,
    isUpdatingRating = false,
    isUpdatingStatus = false,
    talentFavoritesMap = {},
}) => {
    const { clearSelection, selectedTalentIds, showSuggestJobOfferDialog } = useTalentOperations();
    return (
        <FloatingActionBarContainer>
            {/* Selected Count */}
            <SelectedCountContainer onClick={clearSelection}>
                <Checkbox checked size="small" sx={{ color: '#7B6FE8', '&.Mui-checked': { color: '#7B6FE8' }, padding: 0 }} />
                <SelectedCountText>
                    {selectedTalentIds.length} candidat{selectedTalentIds.length > 1 ? 's' : ''} sélectionné{selectedTalentIds.length > 1 ? 's' : ''}
                </SelectedCountText>
            </SelectedCountContainer>

            {/* Actions */}
            <ActionsContainer>
                {/* Avatar with Status */}
                <StatusContainer isUpdating={isUpdatingStatus}>
                    <AvatarStatusFavorite
                        talentIds={selectedTalentIds}
                        currentStatus={status}
                        upercutFavorites={talentFavoritesMap}
                        onStatusChange={(newStatus) => {
                            if (!isUpdatingStatus) {
                                onStatusChange?.(newStatus);
                            }
                        }}
                        shouldStopPropagation={true}
                        disabled={isUpdatingStatus}
                    />
                    {isUpdatingStatus && <LoadingIndicator size={20} />}
                </StatusContainer>

                {/* Rating */}
                <RatingContainer>
                    <StyledRatingSelector
                        isUpdating={isUpdatingRating}
                        value={0}
                        size="large"
                        onChange={(newValue) => {
                            if (onUpdateRating && newValue && !isUpdatingRating) {
                                onUpdateRating(newValue);
                            }
                        }}
                        readOnly={isUpdatingRating}
                    />
                    {isUpdatingRating && <LoadingIndicator size={20} />}
                </RatingContainer>

                {/* Divider */}
                <Divider />

                {/* Suggest Job Offer Button */}
                <JobOfferButton onClick={() => showSuggestJobOfferDialog(selectedTalentIds)}>
                    <BriefcaseIcon className="fi fi-br-briefcase" />
                </JobOfferButton>

                {/* Send Message Button */}
                <SendMessageButton variant="contained" onClick={onSendMessage}>
                    <SendMessageText>
                        Envoyer un message
                    </SendMessageText>
                </SendMessageButton>
            </ActionsContainer>
        </FloatingActionBarContainer>
    );
};

export default TalentFloatingActionBar;
